/**
 * Kasten „zu bestätigen" (#163, Spec 4.1): KI-Vorschläge (`status === 'ki_vorschlag'`) ganz oben
 * in der Staffing-Maske, farbig im Warnton, damit sie nicht übersehen werden. Je Vorschlag Stufe,
 * Satz, PT, Notiz (gekürzt, ausklappbar), Markierung „unsicher" und die Knöpfe Bestätigen /
 * Verwerfen; darüber „Alle bestätigen". Ohne `staffing_manage` bleibt der Kasten sichtbar, die
 * Knöpfe fehlen, ein Hinweis sagt, wer bestätigt.
 *
 * KI-Vorschläge erscheinen NUR hier, nicht in der normalen Positionsliste — so sind sie nie
 * Ablageziel (Drag&Drop) und bieten kein „+ Person besetzen" (Server: 400 `position_unbestaetigt`).
 *
 * „Alle bestätigen" = je Vorschlag ein PATCH nacheinander (kein Sammelendpunkt), Fehler sammeln,
 * danach EINMAL neu laden; schlägt einer fehl, bleiben die übrigen bestätigt und ein Fehler-Toast
 * nennt die Zahl. Braucht den Maskenkontext (`useMaske`) für Projekt und Neuladen.
 */
import type { MaskePosition } from '@projektverwaltung/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { useToast } from '../../toast.js';
import { useConfirm } from '../confirm-dialog.js';
import { maskeQueryKeys, useMaske, useMaskeKnopf, usePreisText } from './maske-kontext.js';

const KNOPF = 'text-xs px-2 py-0.5 rounded disabled:cursor-not-allowed disabled:opacity-60';

export function ZuBestaetigenKasten({
  positionen,
  kannBestaetigen,
}: {
  positionen: MaskePosition[];
  kannBestaetigen: boolean;
}) {
  const { t } = useTranslation();
  const { projectId } = useMaske();
  const qc = useQueryClient();
  const toast = useToast();
  const knopf = useMaskeKnopf();
  const confirmAction = useConfirm();
  const preisText = usePreisText();
  const [laeuft, setLaeuft] = useState(false);
  const [notizOffen, setNotizOffen] = useState<string[]>([]);
  const vorschlaege = positionen.filter((p) => p.status === 'ki_vorschlag');
  if (vorschlaege.length === 0) return null;

  const titel = t('v2.staffing_maske.zu_bestaetigen.titel', { count: vorschlaege.length });

  const alleBestaetigen = async () => {
    setLaeuft(true);
    let fehler = 0;
    const meldungen = new Set<string>();
    for (const p of vorschlaege) {
      try {
        await api.patchBeauftragtePosition(p.beauftragte_position_id, { status: 'bestaetigt' });
      } catch (e) {
        fehler += 1;
        if (e instanceof Error && e.message) meldungen.add(e.message);
      }
    }
    await Promise.all(
      maskeQueryKeys(projectId).map((k) => qc.invalidateQueries({ queryKey: [...k] })),
    );
    setLaeuft(false);
    if (fehler > 0) {
      toast.error(
        t('v2.staffing_maske.zu_bestaetigen.alle_fehler', {
          n: fehler,
          gesamt: vorschlaege.length,
        }),
        meldungen.size > 0 ? [...meldungen].join(' · ') : undefined,
      );
    }
  };

  return (
    <section
      aria-label={titel}
      className="rounded-md px-3 py-2 space-y-1.5"
      style={{
        background: 'var(--staffing-zu_bestaetigen-soft)',
        border: '1px solid var(--staffing-zu_bestaetigen)',
        borderLeftWidth: '4px',
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
          ⚠ {titel}
        </h4>
        {kannBestaetigen ? (
          <button
            type="button"
            className={`${KNOPF} ml-auto font-medium`}
            style={{
              background: 'var(--staffing-zu_bestaetigen)',
              color: 'var(--staffing-zu_bestaetigen-ink)',
            }}
            disabled={laeuft}
            onClick={() => void alleBestaetigen()}
          >
            {t('v2.staffing_maske.zu_bestaetigen.alle')}
          </button>
        ) : (
          <span className="ml-auto text-xs" style={{ color: 'var(--ink-2)' }}>
            {t('v2.staffing_maske.zu_bestaetigen.nur_kfml')}
          </span>
        )}
      </div>
      <ul className="space-y-1">
        {vorschlaege.map((p) => {
          const id = p.beauftragte_position_id;
          const offen = notizOffen.includes(id);
          return (
            <li
              key={id}
              data-testid={`vorschlag-${id}`}
              className="rounded px-2 py-1"
              style={{ background: 'var(--surface)' }}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="font-medium" style={{ color: 'var(--ink)' }}>
                  {p.stufe}
                </span>
                <span style={{ color: 'var(--ink-2)' }}>{preisText(p.preis, p.preis_einheit)}</span>
                <span style={{ color: 'var(--ink-2)' }}>
                  {p.anzahl_pt === null
                    ? '—'
                    : t('v2.staffing_maske.pt', { n: p.anzahl_pt.toLocaleString('de-DE') })}
                </span>
                {p.unsicher && (
                  <span
                    className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.08em]"
                    style={{
                      background: 'var(--staffing-zu_bestaetigen-soft)',
                      color: 'var(--ink)',
                      border: '1px dashed var(--staffing-zu_bestaetigen)',
                      fontFamily: 'var(--f-mono)',
                    }}
                  >
                    {t('v2.staffing_maske.zu_bestaetigen.unsicher')}
                  </span>
                )}
                {kannBestaetigen && (
                  <span className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      className={KNOPF}
                      style={{ color: 'var(--primary)', border: '1px solid var(--primary)' }}
                      disabled={laeuft}
                      aria-label={`${t('v2.staffing_maske.zu_bestaetigen.bestaetigen')}: ${p.stufe}`}
                      onClick={() =>
                        knopf(() => api.patchBeauftragtePosition(id, { status: 'bestaetigt' }))
                      }
                    >
                      {t('v2.staffing_maske.zu_bestaetigen.bestaetigen')}
                    </button>
                    <button
                      type="button"
                      className={KNOPF}
                      style={{ color: 'var(--danger)' }}
                      disabled={laeuft}
                      aria-label={`${t('v2.staffing_maske.zu_bestaetigen.verwerfen')}: ${p.stufe}`}
                      onClick={() =>
                        void confirmAction({
                          title: t('v2.staffing_maske.zu_bestaetigen.verwerfen_frage'),
                          body: p.stufe,
                          confirmLabel: t('v2.staffing_maske.zu_bestaetigen.verwerfen'),
                          tone: 'danger',
                        }).then((ok) => {
                          if (ok) knopf(() => api.deleteBeauftragtePosition(id));
                        })
                      }
                    >
                      {t('v2.staffing_maske.zu_bestaetigen.verwerfen')}
                    </button>
                  </span>
                )}
              </div>
              {p.notiz && (
                <div className="flex items-start gap-2 text-xs" style={{ color: 'var(--ink-2)' }}>
                  <span className={offen ? 'whitespace-pre-wrap' : 'min-w-0 truncate'}>
                    {p.notiz}
                  </span>
                  <button
                    type="button"
                    aria-expanded={offen}
                    className="shrink-0 hover:underline"
                    style={{ color: 'var(--primary)' }}
                    onClick={() =>
                      setNotizOffen((l) => (offen ? l.filter((x) => x !== id) : [...l, id]))
                    }
                  >
                    {offen
                      ? t('v2.staffing_maske.zu_bestaetigen.notiz_weniger')
                      : t('v2.staffing_maske.zu_bestaetigen.notiz_mehr')}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
